// ./src/components/features/TireSets/TireSetSelector/TireSetSelector.tsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Select,
  SelectProps,
  Group,
  Text,
  Stack,
  Box,
  Badge,
  ComboboxProps,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { TireSet, TireSetService, TireSetFilter } from "@/utils/api";
import { TIRE_TYPE_MAP } from "@/config/tireOptions";
import { LoadingIndicator, AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface TireSetSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected tire set ID */
  value?: string | null | undefined;

  /** Callback fired when selection changes: (name, value, tireSet?) */
  onChange?: (name: string, value: string | null, tireSet?: TireSet) => void;

  /**
   * Vehicle ID to load tire sets for.
   * When provided, the component fetches tire sets automatically.
   * When changed, the list is re-fetched.
   */
  carId?: string | null | undefined;

  /**
   * Externally provided tire sets (bypasses internal fetch).
   * When supplied, the component does NOT fetch on its own.
   */
  tireSets?: TireSet[];

  /** Whether tire sets are loading (only used with external tireSets) */
  loading?: boolean;

  /** Custom filter function applied after fetch / on external data */
  tireSetFilter?: (tireSet: TireSet) => boolean;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Props passed to the underlying Combobox */
  comboboxProps?: ComboboxProps;
}

// ============================================
// Option Data Interface
// ============================================

interface TireSetOptionData {
  value: string;
  label: string;
  tireType?: string;
  summary?: string;
  storageLocation?: string;
}

// ============================================
// Component
// ============================================

const TireSetSelectorComponent: React.FC<TireSetSelectorProps> = ({
  name = "tireSetId",
  value,
  onChange,
  carId,
  tireSets: externalTireSets,
  loading: externalLoading = false,
  tireSetFilter,
  placeholder,
  required = false,
  error,
  disabled,
  size = "md",
  label,
  comboboxProps,
  ...selectProps
}) => {
  const { t } = useTranslation(["tireSets", "common"]);

  // ------------------------------------------
  // Internal fetch state (used when no external tireSets provided)
  // ------------------------------------------
  const isExternalData = externalTireSets !== undefined;

  const [internalTireSets, setInternalTireSets] = useState<TireSet[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // Fetch tire sets when carId changes (only when self-fetching)
  useEffect(() => {
    if (isExternalData) return;

    if (!carId) {
      setInternalTireSets([]);
      return;
    }

    let cancelled = false;

    const fetchTireSets = async () => {
      setInternalLoading(true);
      try {
        const filter: TireSetFilter = {
          carId: [carId],
        };
        const result = await TireSetService.list(filter, undefined, [
          { name: "name", order: "ASC" },
        ]);
        if (!cancelled && result?.data) {
          setInternalTireSets(
            Array.isArray(result.data) ? result.data : []
          );
        }
      } catch (err) {
        console.error("TireSetSelector: failed to load tire sets", err);
        if (!cancelled) {
          setInternalTireSets([]);
        }
      } finally {
        if (!cancelled) {
          setInternalLoading(false);
        }
      }
    };

    fetchTireSets();
    return () => {
      cancelled = true;
    };
  }, [carId, isExternalData]);

  // Resolved data sources
  const tireSets = isExternalData ? externalTireSets! : internalTireSets;
  const loading = isExternalData ? externalLoading : internalLoading;

  // Default translations
  const defaultPlaceholder = placeholder || t("common:selectors.tireSet.placeholder");
  const defaultLabel = label;

  // Map for quick lookup
  const tireSetMap = useMemo(() => {
    const map = new Map<string, TireSet>();
    tireSets.forEach((ts) => {
      if (ts.id) {
        map.set(ts.id, ts);
      }
    });
    return map;
  }, [tireSets]);

  // Filter and prepare options
  const options = useMemo((): TireSetOptionData[] => {
    let filtered = tireSets;

    if (tireSetFilter) {
      filtered = filtered.filter(tireSetFilter);
    }

    return filtered
      .filter((ts) => ts.id)
      .map((ts) => {
        const parts: string[] = [];
        if (ts.quantity) parts.push(`${ts.quantity} tires`);
        const firstItem = ts.items?.[0];
        if (firstItem) {
          const itemDesc = [firstItem.brand, firstItem.model, firstItem.tireSize]
            .filter(Boolean)
            .join(" ");
          if (itemDesc) parts.push(itemDesc);
        }

        return {
          value: ts.id!,
          label: ts.name || t("tireSets:list.unnamedSet"),
          tireType: ts.tireType,
          summary: parts.join(" · ") || undefined,
          storageLocation: ts.storageLocation || undefined,
        };
      });
  }, [tireSets, tireSetFilter, t]);

  // Handle change
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      if (selectedValue) {
        const tireSet = tireSetMap.get(selectedValue);
        onChange?.(name, selectedValue, tireSet);
      } else {
        onChange?.(name, null, undefined);
      }
    },
    [name, tireSetMap, onChange]
  );

  // Render option
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const optionData = option as TireSetOptionData;
      const typeOption = optionData.tireType
        ? TIRE_TYPE_MAP[optionData.tireType]
        : undefined;

      return (
        <Group gap="sm" wrap="nowrap" align="flex-start">
          {typeOption && (
            <Box
              pt={4}
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: `var(--mantine-color-${typeOption.color}-5)`,
                flexShrink: 0,
                marginTop: 8,
              }}
            />
          )}
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="sm" fw={500} lineClamp={1}>
                {optionData.label}
              </Text>
              {typeOption && (
                <Badge size="xs" variant="light" color={typeOption.color}>
                  {t(`tireSets:${typeOption.labelKey}`)}
                </Badge>
              )}
            </Group>
            {optionData.summary && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {optionData.summary}
              </Text>
            )}
            {optionData.storageLocation && (
              <Group gap={4} wrap="nowrap">
                <AppIcon iconName="MapPin" size={10} color="gray" />
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {optionData.storageLocation}
                </Text>
              </Group>
            )}
          </Stack>
        </Group>
      );
    },
    [t]
  );

  // Left section icon
  const leftSection = useMemo(() => {
    if (value) {
      const selected = tireSetMap.get(value);
      const typeOption = selected?.tireType
        ? TIRE_TYPE_MAP[selected.tireType]
        : undefined;
      if (typeOption) {
        return (
          <AppIcon
            iconName={typeOption.icon || ''}
            size={18}
            color={`var(--mantine-color-${typeOption.color}-5)`}
          />
        );
      }
    }
    return <AppIcon iconName="Circle" size={18} />;
  }, [value, tireSetMap]);

  // Right section for loading
  const rightSection = useMemo(() => {
    if (loading) {
      return <LoadingIndicator size={size} color="brand" />;
    }
    return selectProps.rightSection;
  }, [loading, size, selectProps.rightSection]);

  // Derived UI state
  const isEmpty = !loading && options.length === 0;
  const noCarSelected = !carId && !isExternalData;

  const selectState = useMemo(() => {
    if (noCarSelected) {
      return {
        data: [] as TireSetOptionData[],
        placeholder: t("common:selectors.tireSet.selectCarFirst"),
        isDisabled: true,
      };
    }

    if (loading && tireSets.length === 0) {
      return {
        data: [] as TireSetOptionData[],
        placeholder: t("common:messages.loading"),
        isDisabled: true,
      };
    }

    if (isEmpty) {
      return {
        data: [] as TireSetOptionData[],
        placeholder: t("common:selectors.tireSet.empty"),
        isDisabled: true,
      };
    }

    return {
      data: options,
      placeholder: defaultPlaceholder,
      isDisabled: disabled || loading,
    };
  }, [noCarSelected, loading, tireSets.length, isEmpty, options, defaultPlaceholder, disabled, t]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={value ?? null}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required}
        required={required}
        allowDeselect={!required}
        disabled={selectState.isDisabled}
        error={error}
        data={selectState.data}
        rightSection={rightSection}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions")}
        searchable
        comboboxProps={comboboxProps}
      />
    </Box>
  );
};

export const TireSetSelector = React.memo(TireSetSelectorComponent);